import React from 'react';
import dollars from '../../dollar-format';
import {Link} from "react-router";
import AgentPipelineDetails from "../AgentPipelineDetails/AgentPipelineDetails";
import {executeQuery} from "../../requests/executeQuery";
import {renderPrintFooter} from "../../utils";
import {STRIVE_AGENT_PIPELINE, OA_AGENT_PIPELINE, OLD_STRIVE_AGENT_PIPELINE, UNASSIGNED_AGENT_PIPELINE} from '../../consts/queries';
import {OVERFLOW_TABLE_WIDTH} from '../../consts/tables';
import {agentPipelineChartOptions} from "./chartOptions";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import highcharts3D from 'highcharts/highcharts-3d.src';
import statusColors from '../../status-colors';
import {getCurrentAccess} from '../../access';
import {isOwnSummitPipelineOnly} from '../../access-levels';

highcharts3D(Highcharts);
Highcharts.setOptions({
    lang: {
        decimalPoint: '.',
        thousandsSep: ',',
    }
});

const OAKey = 'Outside Agent';
const priorKey = 'Prior Agents';
const unassignedKey = 'Unassigned';
const totalKey = 'Total';
// The catch-all rows link to the properties behind them (AgentPipelineBucket).
const bucketSlugs = {[OAKey]: 'outside', [priorKey]: 'prior', [unassignedKey]: 'unassigned'};
const striveTotalKey = 'SUMMIT Total';
const threshold = 1000000;

const columns = [{
    label: 'Agent',
    key: 'key',
}, {
    label: '# of Active Listings',
    printLabel: '# of Listings',
    key: 'activeCount',
    includedKeys: [OAKey, priorKey, striveTotalKey],
    type: 'number',
}, {
    label: 'Total Price',
    key: 'activePrice',
    includedKeys: [OAKey, priorKey, striveTotalKey],
    type: 'dollar',
}, {
    label: 'Gross Potential Fee',
    key: 'grossPotentialFee',
    includedKeys: [OAKey, priorKey, striveTotalKey],
    type: 'dollar',
    shownInChart: true,
    color: statusColors['Active'],
}, {
    label: '# of In Negotiation',
    printLabel: '# of In Neg',
    key: 'negCount',
    includedKeys: [priorKey, striveTotalKey],
    type: 'number',
}, {
    label: 'In Negotiation',
    key: 'negPrice',
    includedKeys: [priorKey, striveTotalKey],
    type: 'dollar',
}, {
    label: 'Gross Fee - In Negotiation',
    printLabel: 'Gross Fee - In Neg',
    key: 'negGrossFee',
    includedKeys: [priorKey],
    type: 'dollar',
    shownInChart: true,
    color: statusColors['In Negotiation'],
}, {
    label: '# of Under Contract',
    printLabel: '# of UC',
    key: 'ucCount',
    includedKeys: [priorKey, striveTotalKey],
    type: 'number',
}, {
    label: 'Under Contract',
    key: 'ucPrice',
    includedKeys: [priorKey, striveTotalKey],
    type: 'dollar',
}, {
    label: 'Gross Fee - Under Contract',
    printLabel: 'Gross Fee - UC',
    key: 'ucGrossFee',
    includedKeys: [priorKey],
    type: 'dollar',
    shownInChart: true,
    color: statusColors['Under Contract'],
}, {
    label: '# Closed YTD',
    key: 'closedCount',
    includedKeys: [priorKey, striveTotalKey],
    type: 'number',
}, {
    label: 'Closed',
    key: 'closedPrice',
    includedKeys: [priorKey, striveTotalKey],
    type: 'dollar',
}, {
    label: 'Gross Fee - Closed',
    key: 'closedGrossFee',
    includedKeys: [priorKey],
    type: 'dollar',
    shownInChart: true,
    color: statusColors['Closed'],
}];

const initializeRow = () => {
    return {
        id: 0,
        activeCount: 0,
        activePrice: 0,
        grossPotentialFee: 0,
        negCount: 0,
        negPrice: 0,
        negGrossFee: 0,
        ucCount: 0,
        ucPrice: 0,
        ucGrossFee: 0,
        closedCount: 0,
        closedPrice: 0,
        closedGrossFee: 0,
        currentAgentData: {},
        noDataFound: false,
    }
};

const findAgentRow = (rows, agentEmail) => {
    const keys = Object.keys(rows);
    if (!keys || !keys.length) {
        return {};
    }

    const foundKey = keys.filter(agent => rows[agent].email && agentEmail && rows[agent].email.toLowerCase() === agentEmail.toLowerCase());
    const agentKey = foundKey && foundKey[0];
    return agentKey ? {[agentKey]: rows[agentKey]} : {};
};

const tableHeaderHeight = 48;

export default class AgentPipeline extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            rows: {},
            width: 1400,
            currentAgentData: {},
            height: tableHeaderHeight,
            isPrint: false,
            printBackground: '',
            agentPipelineChartOptions: null,
        };

        this.beforePrint = this.beforePrint.bind(this);
        this.afterPrint = this.afterPrint.bind(this);
        this.updateTableWidth = this.updateTableWidth.bind(this);

        this.tableRef = React.createRef();
    }

    static buildData(stateRows, data, status) {
        const rows = {...stateRows};

        data.map(row => {
            let fullName = row.AGENT_LAST_NAME + row.AGENT_FIRST_NAME
            if (!rows[fullName]) {
                rows[fullName] = initializeRow();
            }
            if (!rows[fullName].id) {
                rows[fullName].id = row.AGENT_ID;
            }
            if (!rows[fullName].email) {
                rows[fullName].email = row.AGENT_EMAIL;
            }
            if (!rows[fullName].name) {
                rows[fullName].name = row.AGENT_FIRST_NAME.charAt(0) + ". " + row.AGENT_LAST_NAME;
            }
            if (!rows[fullName].lastName) {
                rows[fullName].lastName = row.AGENT_LAST_NAME;
            }

            switch (status) {
                case 'Active': {
                    rows[fullName].activeCount = parseFloat(row.TOTAL_LISTING_COUNT || '0') + parseFloat(row.TOTAL_BUYING_COUNT || '0');
                    rows[fullName].activePrice = parseFloat(row.TOTAL_LIST_PRICE || '0') + parseFloat(row.TOTAL_BUYING_LIST_PRICE || '0');
                    rows[fullName].grossPotentialFee = parseFloat(row.LISTING_GROSS_FEE || '0') + parseFloat(row.BUYING_GROSS_FEE || '0');
                    break;
                }
                case 'In Negotiation': {
                    rows[fullName].negCount = parseFloat(row.TOTAL_LISTING_COUNT || '0') + parseFloat(row.TOTAL_BUYING_COUNT || '0');
                    rows[fullName].negPrice = parseFloat(row.TOTAL_LISTING_NEGOTIATION_PRICE || '0') + parseFloat(row.TOTAL_BUYING_NEGOTIATION_PRICE || '0');
                    rows[fullName].negGrossFee = parseFloat(row.LISTING_NEGOTIATION_GROSS_FEE || '0') + parseFloat(row.BUYING_NEGOTIATION_GROSS_FEE || '0');
                    break;
                }
                case 'Under Contract': {
                    rows[fullName].ucCount = parseFloat(row.TOTAL_LISTING_COUNT || '0') + parseFloat(row.TOTAL_BUYING_COUNT || '0');
                    rows[fullName].ucPrice = parseFloat(row.TOTAL_LISTING_NEGOTIATION_PRICE || '0') + parseFloat(row.TOTAL_BUYING_NEGOTIATION_PRICE || '0');
                    rows[fullName].ucGrossFee = parseFloat(row.LISTING_NEGOTIATION_GROSS_FEE || '0') + parseFloat(row.BUYING_NEGOTIATION_GROSS_FEE || '0');
                    break;
                }
                case 'Closed': {
                    rows[fullName].closedCount = parseFloat(row.TOTAL_LISTING_CLOSED_COUNT || '0') + parseFloat(row.TOTAL_BUYING_CLOSED_COUNT || '0');
                    rows[fullName].closedPrice = parseFloat(row.TOTAL_LISTING_CLOSED_PRICE || '0') + parseFloat(row.TOTAL_BUYING_CLOSED_PRICE || '0');
                    rows[fullName].closedGrossFee = parseFloat(row.LISTING_CLOSED_GROSS_FEE || '0') + parseFloat(row.BUYING_CLOSED_GROSS_FEE || '0');
                    break;
                }
            }
        });

        return rows;
    }

    static buildOAData(stateRows, data, status, key) {
        const rows = {...stateRows};

        if (!rows[key]) {
            rows[key] = initializeRow();
        }

        switch (status) {
            case 'Active': {
                rows[key].activeCount = parseFloat(data.TOTAL_LISTING_COUNT || '0') + parseFloat(data.TOTAL_BUYING_COUNT || '0');
                rows[key].activePrice = parseFloat(data.TOTAL_LIST_PRICE || '0') + parseFloat(data.TOTAL_BUYING_LIST_PRICE || '0');
                rows[key].grossPotentialFee = parseFloat(data.LISTING_GROSS_FEE || '0') + parseFloat(data.BUYING_GROSS_FEE || '0');
                break;
            }
            case 'In Negotiation': {
                rows[key].negCount = parseFloat(data.TOTAL_LISTING_COUNT || '0') + parseFloat(data.TOTAL_BUYING_COUNT || '0');
                rows[key].negPrice = parseFloat(data.TOTAL_LISTING_NEGOTIATION_PRICE || '0') + parseFloat(data.TOTAL_BUYING_NEGOTIATION_PRICE || '0');
                rows[key].negGrossFee = parseFloat(data.LISTING_NEGOTIATION_GROSS_FEE || '0') + parseFloat(data.BUYING_NEGOTIATION_GROSS_FEE || '0');
                break;
            }
            case 'Under Contract': {
                rows[key].ucCount = parseFloat(data.TOTAL_LISTING_COUNT || '0') + parseFloat(data.TOTAL_BUYING_COUNT || '0');
                rows[key].ucPrice = parseFloat(data.TOTAL_LISTING_NEGOTIATION_PRICE || '0') + parseFloat(data.TOTAL_BUYING_NEGOTIATION_PRICE || '0');
                rows[key].ucGrossFee = parseFloat(data.LISTING_NEGOTIATION_GROSS_FEE || '0') + parseFloat(data.BUYING_NEGOTIATION_GROSS_FEE || '0');
                break;
            }
            case 'Closed': {
                rows[key].closedCount = parseFloat(data.TOTAL_LISTING_CLOSED_COUNT || '0') + parseFloat(data.TOTAL_BUYING_CLOSED_COUNT || '0');
                rows[key].closedPrice = parseFloat(data.TOTAL_LISTING_CLOSED_PRICE || '0') + parseFloat(data.TOTAL_BUYING_CLOSED_PRICE || '0');
                rows[key].closedGrossFee = parseFloat(data.LISTING_CLOSED_GROSS_FEE || '0') + parseFloat(data.BUYING_CLOSED_GROSS_FEE || '0');
                break;
            }
        }

        return rows;
    }

    static buildTotalData(stateRows) {
        const rows = {...stateRows, [totalKey]: initializeRow(), [striveTotalKey]: initializeRow()};

        Object.keys(rows[totalKey]).map(columnKey => {
            if (columnKey !== "id") {
                rows[totalKey][columnKey] = Object.keys(rows)
                    .reduce((previous, key) => previous + (typeof rows[key][columnKey] === 'string' ? parseFloat(rows[key][columnKey]) : rows[key][columnKey]), 0);
            }
        });

        // Unassigned is not Summit's either — it is the part of the deal nobody
        // claimed, so it comes off alongside the outside agents' share.
        const notSummit = (columnKey) => (rows[OAKey] ? rows[OAKey][columnKey] : 0) + (rows[unassignedKey] ? rows[unassignedKey][columnKey] : 0);

        rows[striveTotalKey].negGrossFee = rows[totalKey].negGrossFee - notSummit('negGrossFee');
        rows[striveTotalKey].ucGrossFee = rows[totalKey].ucGrossFee - notSummit('ucGrossFee');
        rows[striveTotalKey].closedGrossFee = rows[totalKey].closedGrossFee - notSummit('closedGrossFee');

        return rows;
    }

    static verifyThreshold(series) {
        for (let index = 0; index < series.length; index++) {
            const thresholdPassed = !!series[index].data.filter(value => value > threshold).length;
            if (thresholdPassed) return true;
        }
        return false;
    }

    static buildChartOptions({rows, isAgent, currentAgentData}) {
        const chartRows = isAgent ? currentAgentData : rows;
        if (chartRows) {
            let stateAgentPipelineChartOptions = {};
            const chartRowKeys = Object.keys(chartRows).filter(key => ![priorKey, unassignedKey, totalKey, striveTotalKey].includes(key));
            const chartColumns = columns.filter(column => column.shownInChart);
            stateAgentPipelineChartOptions = agentPipelineChartOptions;
            stateAgentPipelineChartOptions.xAxis.categories = chartRowKeys;

            chartColumns.map((column, index) => {
                stateAgentPipelineChartOptions.series[index] = {
                    name: column.label,
                    color: column.color,
                    data: chartRowKeys.map(key => chartRows[key][column.key]),
                }
            });

            if (isAgent) {
                const thresholdPassed = AgentPipeline.verifyThreshold(stateAgentPipelineChartOptions.series);
                stateAgentPipelineChartOptions.chart.options3d.depth = 60;
                stateAgentPipelineChartOptions.chart.width = 790;
                stateAgentPipelineChartOptions.chart.height = 500;
                stateAgentPipelineChartOptions.yAxis.tickInterval = thresholdPassed ? 500000 : 100000;
                stateAgentPipelineChartOptions.plotOptions.column.depth = 60;
                stateAgentPipelineChartOptions.plotOptions.column.pointWidth = 40;
            }

            return stateAgentPipelineChartOptions;
        }

        return null;
    }

	fetch() {
        Promise.all([
            executeQuery({queryName: STRIVE_AGENT_PIPELINE, params: {STATUS: 'Active'}}),
            executeQuery({queryName: STRIVE_AGENT_PIPELINE, params: {STATUS: 'In Negotiation'}}),
            executeQuery({queryName: STRIVE_AGENT_PIPELINE, params: {STATUS: 'Under Contract'}}),
            executeQuery({queryName: STRIVE_AGENT_PIPELINE, params: {STATUS: 'Closed'}}),
            executeQuery({queryName: OA_AGENT_PIPELINE, params: {STATUS: 'Active'}}),
            executeQuery({queryName: OA_AGENT_PIPELINE, params: {STATUS: 'In Negotiation'}}),
            executeQuery({queryName: OA_AGENT_PIPELINE, params: {STATUS: 'Under Contract'}}),
            executeQuery({queryName: OA_AGENT_PIPELINE, params: {STATUS: 'Closed'}}),
            executeQuery({queryName: OLD_STRIVE_AGENT_PIPELINE, params: {STATUS: 'Active'}}),
            executeQuery({queryName: OLD_STRIVE_AGENT_PIPELINE, params: {STATUS: 'In Negotiation'}}),
            executeQuery({queryName: OLD_STRIVE_AGENT_PIPELINE, params: {STATUS: 'Under Contract'}}),
            executeQuery({queryName: OLD_STRIVE_AGENT_PIPELINE, params: {STATUS: 'Closed'}}),
            executeQuery({queryName: UNASSIGNED_AGENT_PIPELINE, params: {STATUS: 'Active'}}),
            executeQuery({queryName: UNASSIGNED_AGENT_PIPELINE, params: {STATUS: 'In Negotiation'}}),
            executeQuery({queryName: UNASSIGNED_AGENT_PIPELINE, params: {STATUS: 'Under Contract'}}),
            executeQuery({queryName: UNASSIGNED_AGENT_PIPELINE, params: {STATUS: 'Closed'}}),
        ]).then(res => {
            if (res && res.every(result => result && result.success && Array.isArray(result.data))) {
                const activeData = res[0].data;
                const negData = res[1].data;
                const ucData = res[2].data;
                const closedData = res[3].data;
                let rows = {...this.state.rows};
                rows = AgentPipeline.buildData(rows, activeData, 'Active');
                rows = AgentPipeline.buildData(rows, negData, 'In Negotiation');
                rows = AgentPipeline.buildData(rows, ucData, 'Under Contract');
                rows = AgentPipeline.buildData(rows, closedData, 'Closed');
                // Sort Order based on Agent First Name
                //rows = Object.fromEntries(Object.entries(rows).sort((a, b) => a[1].lastName.localeCompare(b[1].lastName)));
                rows = Object.fromEntries(Object.entries(rows).sort((a, b) => {
                    const lastNameComaprision = a[1].lastName.localeCompare(b[1].lastName);
                    if(lastNameComaprision === 0){
                        return a[1].name.localeCompare(b[1].name);
                    }
                    return lastNameComaprision;
                }));
                rows = AgentPipeline.buildOAData(rows, res[4].data[0], 'Active', OAKey);
                rows = AgentPipeline.buildOAData(rows, res[5].data[0], 'In Negotiation', OAKey);
                rows = AgentPipeline.buildOAData(rows, res[6].data[0], 'Under Contract', OAKey);
                rows = AgentPipeline.buildOAData(rows, res[7].data[0], 'Closed', OAKey);
                rows = AgentPipeline.buildOAData(rows, res[8].data[0], 'Active', priorKey);
                rows = AgentPipeline.buildOAData(rows, res[9].data[0], 'In Negotiation', priorKey);
                rows = AgentPipeline.buildOAData(rows, res[10].data[0], 'Under Contract', priorKey);
                rows = AgentPipeline.buildOAData(rows, res[11].data[0], 'Closed', priorKey);
                rows = AgentPipeline.buildOAData(rows, res[12].data[0], 'Active', unassignedKey);
                rows = AgentPipeline.buildOAData(rows, res[13].data[0], 'In Negotiation', unassignedKey);
                rows = AgentPipeline.buildOAData(rows, res[14].data[0], 'Under Contract', unassignedKey);
                rows = AgentPipeline.buildOAData(rows, res[15].data[0], 'Closed', unassignedKey);
                rows = AgentPipeline.buildTotalData(rows);

                const state = {
                    rows,
                };

                if (this.isOwnOnly()) {
                    const currentAgentData = findAgentRow(rows, this.props.userData.email);
                    if (currentAgentData && Object.keys(currentAgentData).length) {
                        state.currentAgentData = currentAgentData;
                    } else {
                        state.noDataFound = 'No data available';
                    }
                }

                state.agentPipelineChartOptions = AgentPipeline.buildChartOptions({rows, isAgent: this.isOwnOnly(), currentAgentData: state.currentAgentData});

                this.setState(state, () => {
                    const headerHeight = 40;
                    const headDetailsHeight = 116;
                    const bottomSpaceHeight = 30;
                    let height = window.innerHeight - headerHeight - headDetailsHeight - bottomSpaceHeight;
                    if (height > this.tableRef.current.clientHeight && this.tableRef.current.clientHeight >= tableHeaderHeight) {
                        height = this.tableRef.current.clientHeight + (this.isOwnOnly() ? 0 : 15);
                    }
                    this.setState({height});
                });
            }
        });
	}

    updateTableWidth() {
        this.setState({width: window.innerWidth - OVERFLOW_TABLE_WIDTH});
    }

    /**
     * Whether the table shows only the signed-in agent's own row: their access
     * level gives them their own Agent Pipeline only (summit.agent_pipeline = own).
     */
    isOwnOnly() {
        return isOwnSummitPipelineOnly(getCurrentAccess());
    }

    componentDidMount() {
        this.updateTableWidth();
        this.setState({printBackground: `url('${arc.path.media+'Transaction_Background.png'}')`}, this.fetch);
        window.addEventListener('resize', this.updateTableWidth);
        window.onbeforeprint = this.beforePrint;
        window.onafterprint = this.afterPrint;
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateTableWidth);
    }

    beforePrint() {
        this.setState({isPrint: true});
    }

    afterPrint() {
        this.setState({isPrint: false});
    }

    static displayValue({type, value, key, includedKeys}) {
        if (!value) {
            if (includedKeys.includes(key))
                return '-';

            switch (type) {
                case 'number':
                    return '0.00';
                case 'dollar':
                    return '$0.00';
            }
        }

        switch (type) {
            case 'number':
                return Number(value).toFixed(2);
            case 'dollar':
                return dollars(value, true);
        }
    }

    renderTable({rows, cols, isAgent, isPrintTable}) {
        return (
            <table className='table' ref={!isPrintTable && this.tableRef}>
                <thead>
                <tr>
                    {
                        cols.map((column, index) => {
                            const label = isPrintTable && column.printLabel ? column.printLabel : column.label;
                            if (index === 0)
                                return <th key={index} className="AgentPipeline_th border_top border_left" id='AgentPipeline_first_cell'>{label}</th>;
                            return <th key={index} className="AgentPipeline_th border_top">{label}</th>
                        })
                    }
                </tr>
                </thead>
                <tbody>
                {
                    Object.keys(rows).map(key => {
                        if (!rows[key]) return;
                        const extraStyle = key === totalKey ? 'total' : (key === striveTotalKey ? 'summit-total' : '');
                        return (
                            <tr key={key} className="AgentPipeline_tr">
                                {
                                    cols.map((column, index) => {
                                        if (index === 0)
                                            return (
                                                <th key={key+'-'+index} className={`AgentPipeline_th agents border_left ${extraStyle}`}>
                                                    {
                                                        rows[key].id && !isAgent ?
                                                            <Link to={{pathname: `${arc.path.base}agent-pipeline/${rows[key].id}`, state: {id: rows[key].id, name: rows[key].name}}}>{rows[key].name}</Link>
                                                            : bucketSlugs[key] && !isAgent ?
                                                            <Link to={{pathname: `${arc.path.base}agent-pipeline/bucket/${bucketSlugs[key]}`}}>{key}</Link>
                                                            :
                                                            key
                                                    }
                                                </th>
                                            );
                                        // A column that lists SUMMIT Total among its includedKeys
                                        // renders '-' there; the rest are the figures this row publishes.
                                        const isSummitFigure = key === striveTotalKey && !column.includedKeys.includes(striveTotalKey);
                                        return (
                                            <td key={key+'-'+index} className={`AgentPipeline_td ${extraStyle}${isSummitFigure ? ' summit-total_figure' : ''}`}>
                                                {AgentPipeline.displayValue({type: column.type, value: rows[key][column.key], key, includedKeys: column.includedKeys})}
                                            </td>
                                        );
                                    })
                                }
                            </tr>
                        );
                    })
                }
                </tbody>
            </table>
        );
    }

    renderPrintPage({rows, cols, date, isPrint, printBackground}) {

        const splitObjectIntoChunks = (obj, chunkSize = 15) => {
            const entries = Object.entries(obj);
            return Array.from({ length: Math.ceil(entries.length / chunkSize) }, (_, i) =>
              Object.fromEntries(entries.slice(i * chunkSize, (i + 1) * chunkSize))
            );
          };
    
        // Split rows into chunks
        const rowChunks = splitObjectIntoChunks(rows);
        return (
            <React.Fragment>
            {rowChunks.map((rowChunk, chunkIndex) => (
                
            <div className='print-container' style={{backgroundImage: isPrint && printBackground}} key={chunkIndex}>
                {chunkIndex === 0 && <div className='AgentPipeline_head_left'>
                    <h3 className='AgentPipeline_h3'>SUMMIT Agent Pipeline</h3>
                    <h4 className='AgentPipeline_h4'>{date}</h4>
                </div>}
                <div className={`table-print-container ${chunkIndex !== 0 && 'marginTop50'}`}>
                    {this.renderTable({rows: rowChunk, cols, isPrintTable: true})}
                </div>
                {renderPrintFooter(isPrint)}
            </div>
           
             ))}
              </React.Fragment>
        )
    }

	render() {
        const isAgent = this.isOwnOnly();
        const {agentPipelineChartOptions, currentAgentData, isPrint, printBackground} = this.state;
		const rows = isAgent ? currentAgentData : this.state.rows;
		const currentAgentKeys = Object.keys(currentAgentData);
		const agentKey = isAgent && currentAgentKeys.length && currentAgentKeys[0];
		const noDataFound = this.state.noDataFound;
		const d = new Date();
		const date = d.getMonth()+1+'/'+d.getDate()+'/'+d.getFullYear();

		return (
			<div className='AgentPipeline'>
                {this.renderPrintPage({rows, cols: columns, date, isPrint, printBackground})}
                <div className='preload-print-background' style={{backgroundImage: printBackground}}/>

                <div className='AgentPipeline_header'>
                    <h3 className='AgentPipeline_h3'>SUMMIT Agent Pipeline</h3>
                    <h4 className='AgentPipeline_h4'>{date}</h4>
                </div>
                <div className='AgentPipeline_scroll_container' style={{width: this.state.width, height: this.state.height}}>
                    {this.renderTable({rows, cols: columns, isAgent, isPrintTable: false})}
                </div>
                {agentPipelineChartOptions &&
                    <div className='AgentPipeline_chart'>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={agentPipelineChartOptions}
                        />
                    </div>
                }
                {noDataFound ? <div className='no-data'>{noDataFound}</div> : null}
                {isAgent && agentKey ? <AgentPipelineDetails id={currentAgentData[agentKey].id} name={currentAgentData[agentKey].name} withoutAgentName={true} userData={this.props.userData}/> : null}
            </div>
		);
	}
}